/*
 * One care home: its name, its tabs, and whichever screen is open.
 *
 * The same view whoever is looking — a carer on a tablet in the home, or the pharmacy at a desk —
 * with the tabs each person may use. The pharmacy reaches it from its own pages, the home's staff
 * straight after signing in.
 */
import { useEffect, useState } from 'react';
import { api } from './api.ts';
import type { Home, User } from './api.ts';
import { AnalyticsScreen } from './analytics.tsx';
import { CdScreen } from './cd.tsx';
import { ChangesScreen } from './changes.tsx';
import { CycleScreen } from './cycle.tsx';
import { DeliveriesScreen } from './deliveries.tsx';
import { MessagesScreen } from './messages.tsx';
import { DashboardScreen, LiveScreen } from './overview.tsx';
import { PrintoutsScreen } from './printouts.tsx';
import { ResidentsScreen } from './residents.tsx';
import { ReturnsScreen } from './returns.tsx';
import { RoundScreen } from './round.tsx';
import { StaffScreen } from './staff.tsx';
import { TasksScreen } from './tasks.tsx';
import { TraysScreen } from './trays.tsx';

/*
 * Printing, in one place. For the pharmacy at a tray home: every seal for a cycle. For everyone:
 * the backup charts and summaries, which are built from what is approved.
 */
function PrintScreen({ homeId, seals }: { homeId: string; seals: boolean }) {
  const [part, setPart] = useState<'seals' | 'backup'>(seals ? 'seals' : 'backup');
  return (
    <>
      {seals ? (
        <nav className="tabs" aria-label="What to print">
          <button type="button" className={part === 'seals' ? 'on' : ''} onClick={() => setPart('seals')}>Seals</button>
          <button type="button" className={part === 'backup' ? 'on' : ''} onClick={() => setPart('backup')}>
            Backup MARs and summaries
          </button>
        </nav>
      ) : null}
      {seals && part === 'seals' ? <TraysScreen homeId={homeId} /> : <PrintoutsScreen homeId={homeId} />}
    </>
  );
}

export const HOME_TABS = [
  'dashboard', 'live', 'analytics', 'round', 'changes', 'cd', 'cycles', 'print',
  'deliveries', 'messages', 'returns', 'tasks', 'residents', 'staff',
] as const;
export type HomeTab = (typeof HOME_TABS)[number];

export function isHomeTab(value: string | undefined): value is HomeTab {
  return (HOME_TABS as readonly string[]).includes(value ?? '');
}

type Props = {
  user: User;
  home: Home;
  // A way back out, for someone who can see more than one home.
  leave?: { label: string; onLeave: () => void } | null;
  initialTab?: HomeTab | null;
  // Open straight onto one delivery, when arriving from the list of every delivery.
  initialDeliveryId?: string | null;
  onTabChange?: (tab: HomeTab) => void;
};

export function HomeView({ user, home, leave, initialTab, initialDeliveryId, onTabChange }: Props) {
  const canManage = user.roles.includes('manage') || user.roles.includes('pharmacy');
  const canRecord = user.roles.includes('meds') || user.roles.includes('creams');
  const canChange = canRecord || canManage;
  // Trays are made up and sealed at the pharmacy; the home sees what is in them on the round.
  const isPharmacy = user.roles.includes('pharmacy');
  const canOversee = canManage || user.roles.includes('check');

  const [tab, setTabState] = useState<HomeTab>(
    initialTab ?? (canManage && !canRecord ? 'dashboard' : canRecord ? 'round' : 'residents'),
  );
  const [unread, setUnread] = useState(0);
  // A resident to open on the Residents tab, when coming from a reported change.
  const [residentToOpen, setResidentToOpen] = useState<string | null>(null);

  const setTab = (next: HomeTab) => {
    setTabState(next);
    onTabChange?.(next);
  };

  // Arriving from somewhere else in the pharmacy's pages can change the tab without remounting.
  useEffect(() => {
    if (initialTab) setTabState(initialTab);
  }, [initialTab]);

  // The message count sits on the tab, so nobody has to remember to look.
  useEffect(() => {
    const look = () =>
      api
        .get<{ unread: number }>(`/api/homes/${home.id}/threads?show=open`)
        .then((data) => setUnread(data.unread))
        .catch(() => setUnread(0));
    void look();
    const timer = window.setInterval(look, 60_000);
    return () => window.clearInterval(timer);
  }, [home.id, tab]);

  const tabButton = (id: HomeTab, label: string) => (
    <button type="button" className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>
      {label}
    </button>
  );

  return (
    <>
      <div className="head">
        <div>
          <h1>{home.name}</h1>
          <p className="muted small">
            {home.supplyMode === 'tray' ? 'Medicines supplied in trays' : 'Medicines supplied in original packs'}
          </p>
        </div>
        {leave ? (
          <button type="button" className="quiet" onClick={leave.onLeave}>{leave.label}</button>
        ) : null}
      </div>

      <nav className="tabs">
        {canManage ? tabButton('dashboard', 'Today') : null}
        {canOversee ? tabButton('live', 'Round board') : null}
        {canOversee ? tabButton('analytics', "How it's going") : null}
        {canRecord ? tabButton('round', 'Medicines round') : null}
        {canChange ? tabButton('changes', 'Changes') : null}
        {canRecord ? tabButton('cd', 'Controlled drugs') : null}
        {canChange ? tabButton('cycles', '28-day cycles') : null}
        {canChange ? tabButton('deliveries', isPharmacy ? 'Deliveries' : 'Booking in') : null}
        {canChange ? tabButton('returns', 'Returns') : null}
        {tabButton('messages', `Messages${unread ? ` (${unread})` : ''}`)}
        {canRecord || canManage ? tabButton('tasks', 'Follow-ups') : null}
        {canOversee || isPharmacy ? tabButton('print', 'Print') : null}
        {tabButton('residents', 'Residents')}
        {canManage ? tabButton('staff', 'Staff logins') : null}
      </nav>

      {canManage && tab === 'dashboard' ? (
        <DashboardScreen homeId={home.id} />
      ) : canOversee && tab === 'live' ? (
        <LiveScreen homeId={home.id} />
      ) : canOversee && tab === 'analytics' ? (
        <AnalyticsScreen homeId={home.id} />
      ) : canManage && tab === 'staff' ? (
        <StaffScreen homeId={home.id} myId={user.id} />
      ) : canRecord && tab === 'round' ? (
        <RoundScreen homeId={home.id} user={user} />
      ) : canChange && tab === 'changes' ? (
        <ChangesScreen
          homeId={home.id}
          user={user}
          onOpenResident={(residentId) => {
            setResidentToOpen(residentId);
            setTab('residents');
          }}
        />
      ) : canRecord && tab === 'cd' ? (
        <CdScreen homeId={home.id} />
      ) : canChange && tab === 'cycles' ? (
        <CycleScreen homeId={home.id} user={user} />
      ) : canChange && tab === 'deliveries' ? (
        <DeliveriesScreen homeId={home.id} user={user} initialOpenId={initialDeliveryId ?? null} />
      ) : canChange && tab === 'returns' ? (
        <ReturnsScreen homeId={home.id} user={user} />
      ) : tab === 'messages' ? (
        <MessagesScreen homeId={home.id} user={user} />
      ) : (canOversee || isPharmacy) && tab === 'print' ? (
        <PrintScreen homeId={home.id} seals={isPharmacy && home.supplyMode === 'tray'} />
      ) : (canRecord || canManage) && tab === 'tasks' ? (
        <TasksScreen homeId={home.id} />
      ) : (
        <ResidentsScreen homeId={home.id} user={user} openResidentId={residentToOpen} />
      )}
    </>
  );
}
